import React, { useContext, useEffect, useState, useMemo } from "react";
import { useMutation } from "react-query";
import { Link } from "react-router-dom";
import client from "../../api";
import { useScratch } from "react-use";
import {
  ILanguagesList,
  IModuleData,
  ISearchTranslations,
  ITranslations,
  ModuleData,
} from "src/api/types";
import CurrencyDrop from "src/components/ui/dropdown-list";
import { m } from "framer-motion";
import { ArrowUpIcon } from "src/components/icons/arrow-up";
import { ArrowDownIcon } from "src/components/icons/arrow-down";
import { EditIcon } from "src/components/icons/edit-icon";
import { SpinnerIcon } from "src/components/icons/spinner-icon";
import { ColumnsType } from "antd/es/table";
import { CheckIcon } from "src/components/icons/check-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { getErrorMessage, logger } from "src/lib/utils";
import Search from "src/components/search-filters/basic-search-filter";
import debounce from "lodash/debounce";
import GlobalSearch from "./globalSearch";
import Button from "../ui/button";

const WebTranslation = () => {
  const { canView, canList, canAdd, canEdit } = usePermissions(
    permissionConstants.TRANSLATION_MOBILE
  );
  const canViewTranslation = canView || canList;

  const [languages, setLanguages] = useState<ILanguagesList[] | undefined>();
  const [languageList, setLanguageList] = useState<string[]>();
  const [selectedLanguage, setSelectedLanguage] = useState({
    name: "English",
    code: "EN",
  });
  const [modulesList, setModulesList] = useState<IModuleData[]>();
  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);
  const [openSubMenuDetails, setOpenSubMenuDetails] = useState<number | null>(
    null
  );
  const [selectedModule, setSelectedModule] = useState<string | undefined>();
  const [selectedSubModule, setSelectedSubModule] = useState<
    string | undefined
  >("");

  const [editRowIndex, setEditRowIndex] = useState<number | null>();
  const [editSubRowIndex, setEditSubRowIndex] = useState<{
    parentIndex: number | null;
    subIndex: number | null;
    nestedSubIndex?: number | null;
  }>({
    parentIndex: null,
    subIndex: null,
    nestedSubIndex: null,
  });
  const [searchText, setSearchText] = useState("");
  const [editValue, setEditValue] = useState<string | undefined>("");
  const [translationDetails, setTranslationDetails] = useState<
    ITranslations | undefined
  >();
  const [searchTranslationDetails, setSearchTranslationDetails] = useState<
    ISearchTranslations[] | undefined
  >();
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  //Add translation
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedUploadLanguage, setSelectedUploadLanguage] = useState({
    name: "English",
    code: "EN",
  });
  const [jsonFile, setJsonFile] = useState<File | null>(null);

  const { mutate: uploadTranslations, isLoading: isUploading } = useMutation(
    client.translations.uploadTranslations,
    {
      onSuccess: (data) => {
        setAlertText(data?.message || "Translation uploaded successfully!");
        setIsAlertOpened(true);
        setIsAddModalOpen(false);
        setJsonFile(null);
      },
      onError: (error: any) => {
        setAlertText(getErrorMessage(error.response?.data));
        setIsAlertOpened(true);
      },
    }
  );

  const handleSelectedUploadLanguage = (e: any) => {
    let langObj = languages?.find((item: any) => item.name === e);
    setSelectedUploadLanguage({
      name: langObj?.name ?? "",
      code: langObj?.code ?? "",
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/json") {
      setJsonFile(file);
    } else {
      setJsonFile(null);
      setAlertText("Please upload a valid JSON file.");
      setIsAlertOpened(true);
    }
  };

  const handleSubmitTranslation = () => {
    if (!canAdd) {
      setAlertText("You do not have permission to add translations");
      setIsAlertOpened(true);
      return;
    }

    if (!selectedUploadLanguage || !jsonFile) {
      setAlertText("Both language and file are required!");
      setIsAlertOpened(true);
      return;
    }

    // Prepare form data
    // Prepare form data
    const formData = new FormData();
    formData.append("file", jsonFile); // 👈 attaches your JSON file
    formData.append("langCode", selectedUploadLanguage.code.toLowerCase());
    formData.append("platform", "app");

    uploadTranslations(formData);
  };

  const { mutate: getLanguages, isLoading: languageLoading } = useMutation(
    client.translations.allLanguages,
    {
      onSuccess(data: any) {
        setLanguages(data.data);
        let list = data.data.map((item: any) => item.name);
        setLanguageList(list);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: getModules, isLoading: modulesLoading } = useMutation(
    client.translations.allModules,
    {
      onSuccess(data: any, variables, context) {
        setModulesList(data.modules);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: getModulesDetails, isLoading: loadingModuleDetails } =
    useMutation(client.translations.fetchModuleDetails, {
      onSuccess(data: any) {
        setTranslationDetails(data);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: updateModuleDetails, isLoading: loadingUpdateModuleDetails } =
    useMutation(client.translations.updateModuleDetails, {
      onSuccess(data) {
        setAlertText("Updated Successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: globalSearch, isLoading: loadingGlobalSearch } = useMutation(
    client.translations.globalSearch,
    {
      onSuccess(data: any) {
        logger.log(data, "SEARCHHH");
        setSearchTranslationDetails(data.data);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const handleLanguage = (e: any) => {
    let langObj = languages?.find((item: any) => item.name === e);
    setSelectedLanguage({
      name: langObj?.name ?? "",
      code: langObj?.code ?? "",
    });
  };

  useEffect(() => {
    if (canViewTranslation) {
      getModules({ language: selectedLanguage.code, platform: "app" });
      if (selectedModule) {
        if (selectedSubModule) {
          handleDetails(selectedModule, selectedSubModule);
        } else {
          handleDetails(selectedModule);
        }
      }
    }
  }, [selectedLanguage, canViewTranslation]);

  useEffect(() => {
    if (canViewTranslation) {
      getLanguages();
    }
  }, [canViewTranslation]);

  const handleDetails = (module: string, nestedModule?: string) => {
    getModulesDetails({
      language_code: selectedLanguage.code,
      platform: "app",
      module: module,
      ...(nestedModule && nestedModule !== undefined
        ? { nested_key: nestedModule }
        : {}),
    });
  };

  const handleSave = (
    module: string,
    key: string,
    nestedModule?: string,
    nestedKey?: string
  ) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit translations");
      setIsAlertOpened(true);
      return;
    }
    logger.log(
      module,
      key,
      nestedModule,
      nestedKey,
      editValue,
      "   ffff   ffff"
    );
    let obj = {
      language_code: selectedLanguage.code,
      platform: "app",
      module: module,
      translations:
        nestedModule && nestedKey
          ? {
              [nestedModule]: {
                [nestedKey]: {
                  [key]: editValue,
                },
              },
            }
          : nestedModule
          ? {
              [nestedModule]: {
                [key]: editValue,
              },
            }
          : {
              [key]: editValue,
            },
    };
    updateModuleDetails(obj);
    if (searchText) {
      debouncedSearch(searchText);
      setEditSubRowIndex({
        parentIndex: null,
        subIndex: null,
        nestedSubIndex: null,
      });
      setEditValue(undefined);
      setEditRowIndex(null);
    } else {
      onCallBack(module);
    }
  };

  const onCallBack = (module: any, nestedModule?: string) => {
    setEditRowIndex(null);
    setEditSubRowIndex({
      parentIndex: null,
      subIndex: null,
      nestedSubIndex: null,
    });

    setTimeout(() => {
      getModulesDetails({
        language_code: selectedLanguage.code,
        platform: "app",
        module: module,
        ...(nestedModule && nestedModule !== undefined
          ? { nested_key: nestedModule }
          : {}),
      });
    }, 500);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string, selectedMod?: string) => {
        globalSearch({
          search: value,
          platform: "app",
          language_code: selectedLanguage.code,
        });
      }, 300),
    [selectedLanguage.code]
  );

  useEffect(() => {
    if (searchText) {
      setSearchTranslationDetails(undefined);
      debouncedSearch(searchText, selectedModule);
    }
  }, [searchText]);

  return (
    <>
      <section className="relative">
        <div className="container px-3">
          {!canViewTranslation ? (
            <div className="flex flex-col items-center justify-center bg-white rounded-[12px] my-6 p-8 text-center shadow-sm">
              <p className="font-manrope text-[16px] font-semibold text-secondary">
                You do not have permission to view translations.
              </p>
            </div>
          ) : languageLoading ? (
            <div className="flex justify-center items-center w-full min-h-[calc(100vh-70px)]">
              <SpinnerIcon className="h-auto w-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid items-start gap-3 grid-cols-12">
                <div className="col-span-3  bg-white sticky top-[70px] overflow-auto scrollbar-hide max-h-[calc(100vh-90px)]">
                  <div
                    className="bg-white shadow-lg p-5 border border-[#ddd] sticky top-[7
                0px]"
                  >
                    <p className="m-0 text-[14px] text-black/50">
                      Choose a language
                    </p>
                    <CurrencyDrop
                      value={selectedLanguage.name}
                      options={languageList ? languageList : []}
                      onChange={handleLanguage}
                      type="language"
                    />
                  </div>
                  <div className="p-3">
                    <p className="m-0 text-[14px] text-black/50">
                      Name of section
                    </p>
                    <ul className="list-none pl-0 mb-0 flex flex-col gap-4 mt-3">
                      {modulesList &&
                        modulesList.length > 0 &&
                        modulesList.map((item: any, index) => (
                          <>
                            <li key={index} className="">
                              <div className="text-black font-medium hover:text-black flex items-center justify-between capitalize">
                                <span
                                  className="cursor-pointer"
                                  onClick={() => {
                                    logger.log(item, " mkwmdkmkdmkdlm");
                                    setSearchTranslationDetails(undefined);
                                    setSelectedSubModule("");
                                    setSearchText("");
                                    setSelectedModule(item.module);
                                    handleDetails(item.module);
                                  }}
                                >
                                  {item.module}{" "}
                                </span>
                                {item.nested_keys.length > 0 &&
                                  (openSubMenu === index ? (
                                    <ArrowDownIcon
                                      className="h-[20px] w-[20px] text-secondary cursor-pointer"
                                      onClick={() => setOpenSubMenu(null)}
                                    />
                                  ) : (
                                    <ArrowUpIcon
                                      className="h-[20px] w-[20px] text-secondary cursor-pointer"
                                      onClick={() => setOpenSubMenu(index)}
                                    />
                                  ))}
                              </div>
                              {item.nested_keys.length > 0 &&
                                openSubMenu === index && (
                                  <ul className="list-none pl-3 mt-3">
                                    {item.nested_keys.map(
                                      (item_1: any, idx: number) => (
                                        <li
                                          key={idx}
                                          className="py-1 cursor-pointer"
                                          onClick={() => {
                                            setSelectedSubModule(item_1);
                                            handleDetails(item.module, item_1);
                                          }}
                                        >
                                          {item_1}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}
                            </li>
                          </>
                        ))}
                    </ul>
                  </div>
                </div>
                <div className="col-span-9 ">
                  <div className="my-4 flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <Search
                        searchText={searchText}
                        setSearchText={setSearchText}
                        placeholder="Search"
                      />
                    </div>
                    <PermissionGate
                      module={[permissionConstants.TRANSLATION_MOBILE]}
                      action="can_add"
                    >
                      <Button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-primary text-white font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 transition-all"
                      >
                        + Add Translation
                      </Button>
                    </PermissionGate>
                  </div>
                  {searchTranslationDetails &&
                  searchTranslationDetails.length > 0 ? (
                    <GlobalSearch
                      details={searchTranslationDetails}
                      editRowIndex={editRowIndex}
                      editValue={editValue}
                      setEditValue={setEditValue}
                      handleSave={handleSave}
                      setEditRowIndex={setEditRowIndex}
                      openSubMenuDetails={openSubMenuDetails}
                      setOpenSubMenuDetails={setOpenSubMenuDetails}
                      editSubRowIndex={editSubRowIndex}
                      setEditSubRowIndex={setEditSubRowIndex}
                      loadingUpdateModuleDetails={loadingUpdateModuleDetails}
                      canEdit={canEdit}
                    />
                  ) : (
                    <div className="bg-white p-5 min-h-[500px]  border border-[#dbdbdb] rounded-[10px]">
                      {selectedModule && (
                        <div className="font-bold text-2xl mb-4">
                          {selectedModule}{" "}
                          {selectedSubModule && ` /${selectedSubModule}`}
                        </div>
                      )}

                      <div className="overflow-auto">
                        {loadingUpdateModuleDetails ? (
                          <div className="flex justify-center items-center w-full min-h-[calc(100vh-70px)]">
                            <SpinnerIcon className="h-auto w-5 animate-spin" />
                          </div>
                        ) : (
                          <table className="w-full text-[16px] font-medium">
                            <tbody>
                              {translationDetails &&
                                Object.entries(
                                  translationDetails.translations
                                ).map(([key, value], index) => {
                                  if (typeof value === "string") {
                                    return (
                                      <tr key={index}>
                                        <td className="py-3  px-2">{key} :</td>
                                        {editRowIndex === index ? (
                                          <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) =>
                                              setEditValue(e.target.value)
                                            }
                                          />
                                        ) : (
                                          <td className="py-3 px-2">{value}</td>
                                        )}
                                        <td className="py-3 px-2">
                                          {editRowIndex === index ? (
                                            <>
                                              <button>
                                                <CheckIcon
                                                  className="w-[20px] h-[20px] text-primary"
                                                  onClick={() => {
                                                    logger.log("kjnsjknsjws");
                                                    handleSave(
                                                      translationDetails.module,
                                                      key,
                                                      translationDetails?.nested_key ||
                                                        ""
                                                    );
                                                  }}
                                                />
                                              </button>
                                              <button>
                                                <CloseIcon
                                                  className="w-[20px] h-[20px] text-primary"
                                                  onClick={() => {
                                                    setEditRowIndex(null);
                                                    setEditValue("");
                                                  }}
                                                />
                                              </button>
                                            </>
                                          ) : canEdit ? (
                                            <button>
                                              <EditIcon
                                                className="w-[20px] h-[20px] text-primary"
                                                onClick={() => {
                                                  setEditRowIndex(index);
                                                  setEditValue(value);
                                                }}
                                              />
                                            </button>
                                          ) : null}
                                        </td>
                                      </tr>
                                    );
                                  } else if (
                                    typeof value === "object" &&
                                    value !== null
                                  ) {
                                    return (
                                      <tr key={index}>
                                        <td className="py-3 px-2" colSpan={3}>
                                          <div className="py-4">
                                            <div className="flex items-center justify-between bg-[#f1f1f1] rounded-xl p-5">
                                              <h4 className="text-xl font-semibold">
                                                {key}
                                              </h4>
                                              {openSubMenuDetails === index ? (
                                                <ArrowDownIcon
                                                  className="h-[20px] w-[20px] text-secondary"
                                                  onClick={() =>
                                                    setOpenSubMenuDetails(null)
                                                  }
                                                />
                                              ) : (
                                                <ArrowUpIcon
                                                  className="h-[20px] w-[20px] text-secondary"
                                                  onClick={() =>
                                                    setOpenSubMenuDetails(index)
                                                  }
                                                />
                                              )}
                                            </div>
                                            <div className="overflow-auto">
                                              <div className="w-full">
                                                {openSubMenuDetails === index &&
                                                  Object.entries(value).map(
                                                    (
                                                      [subKey, subValue],
                                                      idx
                                                    ) => {
                                                      const isObject =
                                                        typeof subValue ===
                                                          "object" &&
                                                        subValue !== null;
                                                      const isEditing =
                                                        editSubRowIndex.parentIndex ===
                                                          index &&
                                                        editSubRowIndex.subIndex ===
                                                          idx;

                                                      return (
                                                        <div
                                                          key={idx}
                                                          className={`${
                                                            !isObject &&
                                                            "flex items-center gap-2 justify-between"
                                                          } border-b border-dashed border-[#ddd]`}
                                                        >
                                                          <div className="p-3">
                                                            {subKey}
                                                          </div>

                                                          {isObject ? (
                                                            Object.entries(
                                                              subValue
                                                            ).map(
                                                              (
                                                                [
                                                                  nestedKey,
                                                                  nestedValue,
                                                                ]: any,
                                                                nestedIdx
                                                              ) => {
                                                                const isNestedEditing =
                                                                  editSubRowIndex.parentIndex ===
                                                                    index &&
                                                                  editSubRowIndex.subIndex ===
                                                                    idx &&
                                                                  editSubRowIndex.nestedSubIndex ===
                                                                    nestedIdx;

                                                                return (
                                                                  <div
                                                                    key={
                                                                      nestedIdx
                                                                    }
                                                                    className="p-3 flex items-center justify-between"
                                                                  >
                                                                    <span>
                                                                      {
                                                                        nestedKey
                                                                      }
                                                                    </span>
                                                                    <span>
                                                                      {isNestedEditing ? (
                                                                        <input
                                                                          type="text"
                                                                          value={
                                                                            editValue
                                                                          }
                                                                          onChange={(
                                                                            e
                                                                          ) =>
                                                                            setEditValue(
                                                                              e
                                                                                .target
                                                                                .value
                                                                            )
                                                                          }
                                                                        />
                                                                      ) : typeof nestedValue ===
                                                                          "string" ||
                                                                        typeof nestedValue ===
                                                                          "number" ? (
                                                                        nestedValue
                                                                      ) : (
                                                                        JSON.stringify(
                                                                          nestedValue
                                                                        )
                                                                      )}
                                                                    </span>
                                                                    {isNestedEditing ? (
                                                                      <>
                                                                        <button>
                                                                          <CheckIcon
                                                                            className="w-[20px] h-[20px] text-primary"
                                                                            onClick={() =>
                                                                              handleSave(
                                                                                translationDetails.module,
                                                                                nestedKey,
                                                                                key,
                                                                                subKey
                                                                              )
                                                                            }
                                                                          />
                                                                        </button>
                                                                        <button>
                                                                          <CloseIcon
                                                                            className="w-[20px] h-[20px] text-primary"
                                                                            onClick={() => {
                                                                              setEditSubRowIndex(
                                                                                {
                                                                                  parentIndex:
                                                                                    null,
                                                                                  subIndex:
                                                                                    null,
                                                                                  nestedSubIndex:
                                                                                    null,
                                                                                }
                                                                              );
                                                                              setEditValue(
                                                                                ""
                                                                              );
                                                                            }}
                                                                          />
                                                                        </button>
                                                                      </>
                                                                    ) : canEdit ? (
                                                                      <button>
                                                                        <EditIcon
                                                                          onClick={() => {
                                                                            setEditSubRowIndex(
                                                                              {
                                                                                parentIndex:
                                                                                  index,
                                                                                subIndex:
                                                                                  idx,
                                                                                nestedSubIndex:
                                                                                  nestedIdx,
                                                                              }
                                                                            );
                                                                            setEditValue(
                                                                              nestedValue
                                                                            );
                                                                          }}
                                                                          className="w-[20px] h-[20px] text-primary"
                                                                        />
                                                                      </button>
                                                                    ) : null}
                                                                  </div>
                                                                );
                                                              }
                                                            )
                                                          ) : (
                                                            <div className="p-3">
                                                              {isEditing ? (
                                                                <input
                                                                  type="text"
                                                                  value={
                                                                    editValue
                                                                  }
                                                                  onChange={(
                                                                    e
                                                                  ) =>
                                                                    setEditValue(
                                                                      e.target
                                                                        .value
                                                                    )
                                                                  }
                                                                />
                                                              ) : (
                                                                <div className="p-3">
                                                                  {subValue}
                                                                </div>
                                                              )}
                                                              <div className="py-3 px-2 text-right">
                                                                {isEditing ? (
                                                                  <>
                                                                    <button>
                                                                      <CheckIcon
                                                                        className="w-[20px] h-[20px] text-primary"
                                                                        onClick={() =>
                                                                          handleSave(
                                                                            translationDetails.module,
                                                                            subKey,
                                                                            key
                                                                          )
                                                                        }
                                                                      />
                                                                    </button>
                                                                    <button>
                                                                      <CloseIcon
                                                                        className="w-[20px] h-[20px] text-primary"
                                                                        onClick={() => {
                                                                          setEditSubRowIndex(
                                                                            {
                                                                              parentIndex:
                                                                                null,
                                                                              subIndex:
                                                                                null,
                                                                              nestedSubIndex:
                                                                                null,
                                                                            }
                                                                          );
                                                                          setEditValue(
                                                                            ""
                                                                          );
                                                                        }}
                                                                      />
                                                                    </button>
                                                                  </>
                                                                ) : canEdit ? (
                                                                  <button>
                                                                    <EditIcon
                                                                      onClick={() => {
                                                                        setEditSubRowIndex(
                                                                          {
                                                                            parentIndex:
                                                                              index,
                                                                            subIndex:
                                                                              idx,
                                                                          }
                                                                        );
                                                                        setEditValue(
                                                                          subValue
                                                                        );
                                                                      }}
                                                                      className="w-[20px] h-[20px] text-primary"
                                                                    />
                                                                  </button>
                                                                ) : null}
                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  }
                                })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </section>
      {canAdd && isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-[400px]">
            <h2 className="text-xl font-semibold mb-4">Add New Translation</h2>

            <div className="mb-4">
              <p className="text-sm text-black/60 mb-1">Choose Language</p>
              <CurrencyDrop
                value={selectedUploadLanguage.name}
                options={languageList ?? []}
                onChange={handleSelectedUploadLanguage}
                type="language"
              />
            </div>

            <div className="mb-4">
              <p className="text-sm text-black/60 mb-1">Upload JSON File</p>
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg cursor-pointer focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setJsonFile(null);
                }}
                className="border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-xl px-4 py-2 transition-all"
              >
                Cancel
              </Button>

              <Button
                onClick={!isUploading ? handleSubmitTranslation : undefined}
                disabled={isUploading}
                className={`${
                  isUploading
                    ? "bg-primary/60 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90"
                } text-white font-semibold px-5 py-2 rounded-xl transition-all flex items-center justify-center gap-2`}
              >
                {isUploading && (
                  <SpinnerIcon className="h-4 w-4 animate-spin" />
                )}
                {isUploading ? "Uploading..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WebTranslation;
